import axios from 'axios';

/**
 * API Service for Farm Monitor Admin Panel
 * 
 * Centralized service for all API communications with the backend.
 * Handles authentication, error handling, and request/response transformations.
 */
class ApiService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api/v1';
    this.timeout = parseInt(process.env.REACT_APP_API_TIMEOUT) || 30000;
    
    // Create axios instance
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: this.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => {
        return response.data;
      },
      (error) => {
        if (error.response?.status === 401) {
          this.clearAuthToken();
          window.location.href = '/login';
        }
        return Promise.reject(this.handleError(error));
      }
    );
  }

  /**
   * Handle API errors
   */
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      return {
        success: false,
        message: error.response.data?.message || 'Server error occurred',
        status: error.response.status,
        data: error.response.data,
      };
    } else if (error.request) {
      console.log(error.request);
      // Request made but no response received
      return {
        success: false,
        message: 'Network error - please check your connection',
        status: 0,
      };
    } else {
      // Something else happened
      return {
        success: false,
        message: error.message || 'An unexpected error occurred',
        status: 0,
      };
    }
  }

  /**
   * Authentication methods
   */
  setAuthToken(token) {
    if (token) {
      localStorage.setItem('admin_auth_token', token);
    } else {
      localStorage.removeItem('admin_auth_token');
    }
  }

  getAuthToken() {
    return localStorage.getItem('admin_auth_token');
  }

  clearAuthToken() {
    localStorage.removeItem('admin_auth_token');
    localStorage.removeItem('admin_user');
    localStorage.removeItem('admin_profile');
  }

  isAuthenticated() {
    const token = this.getAuthToken();
    return !!token;
  }

  /**
   * Auth API endpoints
   */
  async login(credentials) {
    try {
      // Handle different login types
      let endpoint;
      if (credentials.type === 'admin') {
        endpoint = '/admin/login';
      } else {
        endpoint = '/auth/google';
      }

      const response = await this.client.post(endpoint, credentials);
      if (response.success && response.data.access_token) {
        this.setAuthToken(response.data.access_token);
        
        // Store both user and admin data
        localStorage.setItem('admin_user', JSON.stringify(response.data.user));
        if (response.data.admin) {
          localStorage.setItem('admin_profile', JSON.stringify(response.data.admin));
        }
      }
      return response;
    } catch (error) {
      throw error;
    }
  }

  async logout() {
    this.clearAuthToken();
    return { success: true };
  }

  getCurrentUser() {
    const userStr = localStorage.getItem('admin_user');
    return userStr ? JSON.parse(userStr) : null;
  }

  getCurrentAdminProfile() {
    const adminStr = localStorage.getItem('admin_profile');
    return adminStr ? JSON.parse(adminStr) : null;
  }

  async getCurrentUserDetails() {
    return this.client.get('/admin/profile');
  }

  /**
   * Users Management API
   */
  async getUsers(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.client.get(`/admin/users${queryString ? `?${queryString}` : ''}`);
  }

  async getUser(userId) {
    return this.client.get(`/admin/users/${userId}`);
  }

  async updateUser(userId, data) {
    return this.client.put(`/admin/users/${userId}`, data);
  }

  async deleteUser(userId) {
    return this.client.delete(`/admin/users/${userId}`);
  }

  async createAdmin(data) {
    return this.client.post('/admin/admins', data);
  }

  /**
   * Farms Management API
   */
  async getFarms(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.client.get(`/admin/farms${queryString ? `?${queryString}` : ''}`);
  }

  async getFarm(farmId) {
    return this.client.get(`/admin/farms/${farmId}`);
  }

  async createFarm(data) {
    return this.client.post('/admin/farms', data);
  }

  async updateFarm(farmId, data) {
    return this.client.put(`/admin/farms/${farmId}`, data);
  }

  async deleteFarm(farmId) {
    return this.client.delete(`/admin/farms/${farmId}`);
  }

  async uploadFarmsCSV(file) {
    const formData = new FormData();
    formData.append('file', file);

    return this.client.post('/admin/farms/csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async generateFarmQRCode(farmId) {
    return this.client.post('/qr-code', { farm_id: farmId });
  }

  async generatePlotQRCode(plotId) {
    return this.client.post('/qr-code', { plot_id: plotId });
  }

  async generateBedQRCode(bedId) {
    return this.client.post('/qr-code', { bed_id: bedId });
  }

  async getFarmPublic(farmId) {
    try {
      const response = await axios.get(`${this.baseURL}/farms/${farmId}`, {
        timeout: this.timeout,
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        return {
          success: false,
          message: error.response.data?.message || 'Server error occurred',
          status: error.response.status,
          data: error.response.data,
        };
      } else if (error.request) {
        return {
          success: false,
          message: 'Network error - please check your connection',
          status: 0,
        };
      } else {
        return {
          success: false,
          message: error.message || 'An unexpected error occurred',
          status: 0,
        };
      }
    }
  }

  /**
   * Plots Management API
   */
  async getPlots(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.client.get(`/plots${queryString ? `?${queryString}` : ''}`);
  }

  async getPlot(plotId) {
    return this.client.get(`/plots/${plotId}`);
  }

  async createPlot(data) {
    return this.client.post('/plots', data);
  }

  async updatePlot(plotId, data) {
    return this.client.put(`/plots/${plotId}`, data);
  }

  async deletePlot(plotId) {
    return this.client.delete(`/plots/${plotId}`);
  }

  async getPlotPublic(plotId) {
    try {
      const response = await axios.get(`${this.baseURL}/plots/${plotId}`, {
        timeout: this.timeout,
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        return {
          success: false,
          message: error.response.data?.message || 'Server error occurred',
          status: error.response.status,
          data: error.response.data,
        };
      } else if (error.request) {
        return {
          success: false,
          message: 'Network error - please check your connection',
          status: 0,
        };
      } else {
        return {
          success: false,
          message: error.message || 'An unexpected error occurred',
          status: 0,
        };
      }
    }
  }

  /**
   * Beds Management API
   */
  async getBeds(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.client.get(`/beds${queryString ? `?${queryString}` : ''}`);
  }

  async getBed(bedId) {
    return this.client.get(`/beds/${bedId}`);
  }

  async createBed(data) {
    return this.client.post('/beds', data);
  }

  async updateBed(bedId, data) {
    return this.client.put(`/beds/${bedId}`, data);
  }

  async deleteBed(bedId) {
    return this.client.delete(`/beds/${bedId}`);
  }

  /**
   * Plants Management API
   */
  async getPlants(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.client.get(`/plants${queryString ? `?${queryString}` : ''}`);
  }

  async getPlant(plantId) {
    return this.client.get(`/plants/${plantId}`);
  }

  async createPlant(data) {
    return this.client.post('/plants', data);
  }

  async updatePlant(plantId, data) {
    return this.client.put(`/plants/${plantId}`, data);
  }

  async deletePlant(plantId) {
    return this.client.delete(`/plants/${plantId}`);
  }

  async getPlantPublic(plantId) {
    try {
      const response = await axios.get(`${this.baseURL}/plants/${plantId}`, {
        timeout: this.timeout,
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        return {
          success: false,
          message: error.response.data?.message || 'Server error occurred',
          status: error.response.status,
          data: error.response.data,
        };
      } else if (error.request) {
        return {
          success: false,
          message: 'Network error - please check your connection',
          status: 0,
        };
      } else {
        return {
          success: false,
          message: error.message || 'An unexpected error occurred',
          status: 0,
        };
      }
    }
  }

  async getBedPublic(bedId) {
    try {
      const response = await axios.get(`${this.baseURL}/beds/${bedId}`, {
        timeout: this.timeout,
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        return {
          success: false,
          message: error.response.data?.message || 'Server error occurred',
          status: error.response.status,
          data: error.response.data,
        };
      } else if (error.request) {
        return {
          success: false,
          message: 'Network error - please check your connection',
          status: 0,
        };
      } else {
        return {
          success: false,
          message: error.message || 'An unexpected error occurred',
          status: 0,
        };
      }
    }
  }


  /**
   * Submissions Management API
   */
  async getSubmissions(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.client.get(`/admin/submissions${queryString ? `?${queryString}` : ''}`);
  }

  async getSubmission(submissionId) {
    return this.client.get(`/admin/submissions/${submissionId}`);
  }

  async updateSubmission(submissionId, data) {
    return this.client.put(`/admin/submissions/${submissionId}`, data);
  }

  async updateSubmissionStatus(submissionId, data) {
    return this.client.put(`/admin/submissions/${submissionId}`, data);
  }

  async deleteSubmission(submissionId) {
    return this.client.delete(`/admin/submissions/${submissionId}`);
  }

  async retrySocialPost(submissionId) {
    return this.client.post(`/submissions/${submissionId}/social-retry`);
  }

  async repostToSocialMedia(submissionId, platform) {
    return this.client.post(`/submissions/${submissionId}/social-repost`, { platform });
  }

  async triggerSocialPost(submissionId) {
    return this.client.post(`/submissions/${submissionId}/trigger-social`);
  }

  // TikTok OAuth
  async getTikTokAuthURL() {
    return this.client.get('/admin/tiktok/auth-url');
  }

  async connectTikTok(code) {
    return this.client.post('/admin/tiktok/callback', { code });
  }

  async getTikTokStatus() {
    return this.client.get('/admin/tiktok/status');
  }

  async disconnectTikTok() {
    return this.client.post('/admin/tiktok/disconnect');
  }

  async getTikTokUserShareAuthURL(submissionId, codeChallenge) {
    return this.client.get(`/tiktok/user-share-auth-url?submission_id=${submissionId}&code_challenge=${encodeURIComponent(codeChallenge)}`);
  }

  async userShareToTikTok(code, submissionId, codeVerifier) {
    return this.client.post('/tiktok/user-share', { code, submission_id: submissionId, code_verifier: codeVerifier });
  }

  async createSubmission(data) {
    return this.client.post('/submissions', data);
  }


  /**
   * Analytics and Dashboard API
   */
  async getDashboardStats() {
    return this.client.get('/admin/dashboard/stats');
  }

  async getAnalytics(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.client.get(`/admin/analytics${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * System Management API
   */
  async getSystemHealth() {
    return this.client.get('/admin/system/health');
  }

  async exportData(type, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await this.client.get(`/admin/export/${type}${queryString ? `?${queryString}` : ''}`, {
      responseType: 'blob'
    });
    return response;
  }

  /**
   * File Upload API
   */
  async uploadFile(file, onProgress) {
    const formData = new FormData();
    formData.append('file', file);

    return this.client.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        if (onProgress) {
          onProgress(progress);
        }
      },
    });
  }

  async uploadMedia(file, fileType, submissionId, onProgress) {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('file_type', fileType);
    formData.append('submission_id', submissionId);

    return this.client.post('/media/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
  }
}

// Create and export singleton instance
const apiService = new ApiService();
export default apiService;